import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { el, enUS } from 'date-fns/locale'
import {
  Users,
  CalendarCheck,
  ClipboardList,
  CreditCard,
  Calendar,
  ArrowRight,
  Phone,
  ExternalLink,
  Trees,
  Info,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useAuthStore } from '@/stores/auth'
import { fetchChildren, fetchBookings, fetchUpcomingEvents, fetchMedicalHistories, fetchPayments, type Event } from '@/api/client'

export function DashboardPage() {
  const { t, i18n } = useTranslation(['home', 'common', 'upcomingPeriods', 'bookings', 'children', 'medicalHistory', 'payments'])
  const { parent } = useAuthStore()
  const dateLocale = i18n.language === 'el' ? el : enUS

  const { data: children = [] } = useQuery({
    queryKey: ['children'],
    queryFn: fetchChildren,
    enabled: !!parent?.id,
  })

  const { data: bookings = [] } = useQuery({
    queryKey: ['bookings'],
    queryFn: fetchBookings,
    enabled: !!parent?.id,
  })

  const { data: events = [] } = useQuery({
    queryKey: ['upcoming-events'],
    queryFn: fetchUpcomingEvents,
    enabled: parent?.is_profile_complete ?? false,
  })

  const { data: medicalHistories = [] } = useQuery({
    queryKey: ['medical-histories'],
    queryFn: fetchMedicalHistories,
    enabled: !!parent?.id,
  })

  const { data: payments = [] } = useQuery({
    queryKey: ['payments'],
    queryFn: fetchPayments,
    enabled: !!parent?.id,
  })

  const [showContactModal, setShowContactModal] = React.useState(false)

  const currentYear = new Date().getFullYear()

  // Filter to current year only for dashboard metrics
  const currentYearBookings = bookings.filter(b => new Date(b.created_at).getFullYear() === currentYear)
  const activeBookings = currentYearBookings.filter(b => !['cancelled', 'completed'].includes(b.status))
  const currentYearMedical = medicalHistories.filter(mh => mh.year === currentYear)
  const paymentEligible = currentYearBookings.filter(b => b.is_payment_eligible)
  const successfulPayments = payments
    .filter(p => p.status === 'success' && new Date(p.created_at).getFullYear() === currentYear)

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <Card className="relative overflow-hidden">
        <div className="absolute -right-4 -top-4 opacity-[0.07]">
          <Trees className="h-36 w-36 text-primary" strokeWidth={1} />
        </div>
        <CardContent className="p-6 relative">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-primary/10 p-2.5 mt-0.5">
              <Trees className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {t('welcome.title')}
              </h1>
              <p className="mt-1 text-muted-foreground max-w-xl">
                {t('welcome.description')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div>
        <p className="text-xs text-muted-foreground mb-2">
          {t('welcome.statsNote', { year: currentYear })}
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 -mt-4">
        <Link to="/children">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="rounded-lg bg-sky-100 p-2.5">
                <Users className="h-5 w-5 text-sky-700" />
              </div>
              <div>
                <p className="text-2xl font-bold">{children.length}</p>
                <p className="text-xs text-muted-foreground">{t('childrenBox.title', { ns: 'children' })}</p>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/bookings">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="rounded-lg bg-violet-100 p-2.5">
                <CalendarCheck className="h-5 w-5 text-violet-700" />
              </div>
              <div>
                <p className="text-2xl font-bold">{activeBookings.length}</p>
                <p className="text-xs text-muted-foreground">{t('bookingsBox.title', { ns: 'bookings' })}</p>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/medical-history">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="rounded-lg bg-emerald-100 p-2.5">
                <ClipboardList className="h-5 w-5 text-emerald-700" />
              </div>
              <div>
                <p className="text-2xl font-bold">{currentYearMedical.length}</p>
                <p className="text-xs text-muted-foreground">{t('title', { ns: 'medicalHistory' })}</p>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/payments">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="rounded-lg bg-amber-100 p-2.5">
                <CreditCard className="h-5 w-5 text-amber-700" />
              </div>
              <div>
                <p className="text-2xl font-bold">{successfulPayments.length}</p>
                <p className="text-xs text-muted-foreground">{t('navigation.payments', { ns: 'common' })}</p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Events */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Calendar className="h-5 w-5 text-primary" />
                {t('title', { ns: 'upcomingPeriods' })}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {events.length > 0 ? (
              <div className="space-y-3">
                {events.map((event: Event) => (
                  <Link
                    key={event.id}
                    to="/bookings"
                    className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                  >
                    <div className="rounded-md bg-primary/10 p-2 mt-0.5">
                      <Calendar className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{event.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {format(new Date(event.start_date), 'PPP', { locale: dateLocale })} - {format(new Date(event.end_date), 'PPP', { locale: dateLocale })}
                      </p>
                      {event.is_full && (
                        <Badge variant="warning" className="mt-1.5 text-xs">
                          {t('waitingList', { ns: 'upcomingPeriods' })}
                        </Badge>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-6">
                {t('noEvents', { ns: 'upcomingPeriods' })}
              </p>
            )}
            <div className="flex items-start gap-2 p-2 bg-muted rounded-md text-xs text-muted-foreground mt-3">
              <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>{t('bookingNote', { ns: 'bookings' })}</span>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions & Contact */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t('welcome.title')}</CardTitle>
            <CardDescription>{t('welcome.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button variant="outline" className="w-full justify-start" onClick={() => setShowContactModal(true)}>
              <Phone className="h-4 w-4 mr-2" />
              {t('welcome.contactUs')}
            </Button>
            <Button variant="outline" className="w-full justify-start" onClick={() => window.open('https://ranch.gr', '_blank')}>
              <ExternalLink className="h-4 w-4 mr-2" />
              {t('welcome.findOutMore')}
            </Button>

            {/* Active bookings needing payment */}
            {paymentEligible.length > 0 && (
              <div className="mt-4 p-3 rounded-lg bg-primary/5 border border-primary/20">
                <p className="text-sm font-medium text-primary">
                  {paymentEligible.length} {t('bookingsBox.title', { ns: 'bookings' }).toLowerCase()} {t('toGetStarted', { ns: 'common' })}
                </p>
                <Button size="sm" className="mt-2" onClick={() => window.location.href = '/payments/checkout'}>
                  {t('navigation.makePayment', { ns: 'common' })}
                  <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowContactModal(false)}>
          <Card className="max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle>{t('contact.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold text-sm mb-1">{t('contact.ranchLocation')}</h3>
                <p className="text-sm text-muted-foreground">{t('contact.ranchAddress')}</p>
                <p className="text-sm mt-1"><span className="font-medium">T:</span> +30 2741093800</p>
              </div>
              <div>
                <h3 className="font-semibold text-sm mb-1">{t('contact.centralOffice')}</h3>
                <p className="text-sm text-muted-foreground">{t('contact.officeAddress')}</p>
                <p className="text-sm mt-1"><span className="font-medium">T:</span> +30 2104100121 / +30 2104100122</p>
              </div>
              <div>
                <h3 className="font-semibold text-sm mb-1">{t('contact.emailAddress')}</h3>
                <p className="text-sm"><span className="font-medium">E:</span> info@ranch.gr</p>
              </div>
              <p className="text-xs text-muted-foreground">{t('contact.workingHours')}</p>
              <Button variant="outline" className="w-full" onClick={() => setShowContactModal(false)}>
                {t('cancel', { ns: 'common' })}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
